import React from 'react';
import {
  Box,
  Typography,
  Container,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

const CookiesPolicy = () => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        background: 'rgba(18, 18, 18, 0.95)',
        minHeight: '100vh',
        color: 'white',
        pt: { xs: 10, sm: 12 },
        pb: 4,
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          px: { xs: 2, sm: 3 },
          py: 4,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 300,
            mb: 2,
            letterSpacing: '0.05em',
            color: '#2196F3',
          }}
        >
          {t('cookies.title')}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}
        >
          {t('cookies.lastUpdated')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}
        >
          {t('cookies.intro1')}
          <MuiLink
            href="https://www.privacypolicies.com/cookies-policy-generator/"
            target="_blank"
            rel="nofollow noopener"
            sx={{ color: '#2196F3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Cookies Policy Generator
          </MuiLink>
          .
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}
        >
          {t('cookies.intro2')}
          <MuiLink
            component={RouterLink}
            to="/privacy-policy"
            sx={{ color: '#2196F3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {t('cookies.privacyPolicy')}
          </MuiLink>
          .
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 4 }}
        >
          {t('cookies.intro3')}
        </Typography>

        <Typography
          variant="h5"
          sx={{ fontWeight: 300, mb: 2, color: '#2196F3' }}
        >
          {t('cookies.interpretationTitle')}
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: 300, mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}
        >
          {t('cookies.interpretation')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}
        >
          {t('cookies.interpretationText')}
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: 300, mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}
        >
          {t('cookies.definitions')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}
        >
          {t('cookies.definitionsIntro')}
        </Typography>
        <List sx={{ pl: 2, mb: 4, listStyleType: 'disc', display: 'list-item' }}>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  <strong>{t('cookies.definitionsCompanyTerm')}</strong>{' '}
                  {t('cookies.definitionsCompany')}
                </Typography>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  <strong>{t('cookies.definitionsCookiesTerm')}</strong>{' '}
                  {t('cookies.definitionsCookies')}
                </Typography>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  <strong>{t('cookies.definitionsWebsiteTerm')}</strong>{' '}
                  {t('cookies.definitionsWebsite')}
                  <MuiLink
                    href="https://aerocycling-project-front.onrender.com/"
                    target="_blank"
                    rel="external nofollow noopener"
                    sx={{ color: '#2196F3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    https://aerocycling-project-front.onrender.com/
                  </MuiLink>
                </Typography>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  <strong>{t('cookies.definitionsYouTerm')}</strong>{' '}
                  {t('cookies.definitionsYou')}
                </Typography>
              }
            />
          </ListItem>
        </List>

        <Typography
          variant="h5"
          sx={{ fontWeight: 300, mb: 2, color: '#2196F3' }}
        >
          {t('cookies.useTitle')}
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: 300, mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}
        >
          {t('cookies.typesTitle')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}
        >
          {t('cookies.typesIntro')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}
        >
          {t('cookies.typesPurpose')}
        </Typography>
        <List sx={{ pl: 2, mb: 4, listStyleType: 'disc', display: 'list-item' }}>
          <ListItem sx={{ display: 'list-item', pl: 1, flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 400, color: 'rgba(255, 255, 255, 0.9)' }}
            >
              <strong>{t('cookies.necessaryTitle')}</strong>
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
            >
              {t('cookies.necessaryType')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
            >
              {t('cookies.necessaryAdmin')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
            >
              {t('cookies.necessaryPurpose')}
            </Typography>
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1, flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 400, color: 'rgba(255, 255, 255, 0.9)' }}
            >
              <strong>{t('cookies.functionalityTitle')}</strong>
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
            >
              {t('cookies.functionalityType')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
            >
              {t('cookies.functionalityAdmin')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
            >
              {t('cookies.functionalityPurpose')}
            </Typography>
          </ListItem>
        </List>

        <Typography
          variant="h6"
          sx={{ fontWeight: 300, mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}
        >
          {t('cookies.choicesTitle')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}
        >
          {t('cookies.choices1')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}
        >
          {t('cookies.choices2')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}
        >
          {t('cookies.choices3')}
        </Typography>
        <List sx={{ pl: 2, mb: 4, listStyleType: 'disc', display: 'list-item' }}>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  {t('cookies.browserChrome')}
                  <MuiLink
                    href="https://support.google.com/accounts/answer/32050"
                    target="_blank"
                    rel="nofollow noopener"
                    sx={{ color: '#2196F3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    https://support.google.com/accounts/answer/32050
                  </MuiLink>
                </Typography>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  {t('cookies.browserIE')}
                  <MuiLink
                    href="http://support.microsoft.com/kb/278835"
                    target="_blank"
                    rel="nofollow noopener"
                    sx={{ color: '#2196F3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    http://support.microsoft.com/kb/278835
                  </MuiLink>
                </Typography>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  {t('cookies.browserFirefox')}
                  <MuiLink
                    href="https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored"
                    target="_blank"
                    rel="nofollow noopener"
                    sx={{ color: '#2196F3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored
                  </MuiLink>
                </Typography>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  {t('cookies.browserSafari')}
                  <MuiLink
                    href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
                    target="_blank"
                    rel="nofollow noopener"
                    sx={{ color: '#2196F3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac
                  </MuiLink>
                </Typography>
              }
            />
          </ListItem>
        </List>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 4 }}
        >
          {t('cookies.browserOther')}
        </Typography>

        <Typography
          variant="h6"
          sx={{ fontWeight: 300, mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}
        >
          {t('cookies.moreInfoTitle')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 4 }}
        >
          {t('cookies.moreInfo')}
          <MuiLink
            href="https://www.privacypolicies.com/blog/cookies/"
            target="_blank"
            sx={{ color: '#2196F3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            What Are Cookies?
          </MuiLink>
          .
        </Typography>

        <Typography
          variant="h6"
          sx={{ fontWeight: 300, mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}
        >
          {t('cookies.contactTitle')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}
        >
          {t('cookies.contactIntro')}
        </Typography>
        <List sx={{ pl: 2, mb: 4, listStyleType: 'disc', display: 'list-item' }}>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 300, color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  {t('cookies.contactEmail')}
                </Typography>
              }
            />
          </ListItem>
        </List>
      </Container>
    </Box>
  );
};

export default CookiesPolicy;